// Convert an overrunning session to an inpatient stay (spec Q10): same
// admission, same bill. Three existing calls, in order:
//   1. raise a cross-ward bed transfer to a ward bed (approved and executed on
//      the existing Bed Transfer screen, as for any inpatient);
//   2. change the admission type from Day Care to General through the IPD
//      module's own registration submit (it updates the open admission);
//   3. mark the Day Care session converted (the server checks step 2 happened).
// A retry after step 2 or 3 failed reuses the transfer already raised; step 2
// is an update of the same admission, so repeating it is harmless.

import React, { useEffect, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { markConverted } from "../api/daycareApi";
import { getTransferRequests, raiseTransferRequest } from "../../ipd/api/bedTransferRequestApi";
import {
  fetchIpdBedTypes,
  fetchIpdBeds,
  fetchPatientAdmissionData,
  submitIpdPatientRegistration,
} from "../../ipd/pages/patient-admission-ipd/api/registrationService";
import { mapAdmissionDataToPatient } from "../../ipd/pages/patient-admission-ipd/utils/mapAdmissionDataToPatient";
import { IPD_COLORS, IpdButton, IpdFormField, IpdSectionCard, IpdTextField } from "../../ipd/utils";

// Bed-transfer requisition statuses still waiting to be carried out.
const OPEN_TRANSFER_STATUSES = ["raised", "approved"];

async function openTransferFor(admissionId) {
  const requests = await getTransferRequests({ admissionId });
  return requests.find((r) => String(r.admission_id) === String(admissionId) && OPEN_TRANSFER_STATUSES.includes(r.status));
}

async function changeAdmissionToGeneral(session) {
  const admission = await fetchPatientAdmissionData(session.patient_id, { silent: true });
  const mapped = mapAdmissionDataToPatient(admission.data, {
    patient_id: session.patient_id, uhid: admission.uhid, admission_id: admission.admission_id,
  });
  const patient = {
    ...mapped,
    encounter: {
      ...mapped.encounter,
      admissionType: "GENERAL",
      bedId: session.bed_code,
      // The bed is already allocated: re-sending "reserved" would flip it.
      newBedStatus: "occupied",
    },
  };
  await submitIpdPatientRegistration(patient, { selectedPatientIdForSearch: session.patient_id });
}

export default function ConvertPanel({ session, wardBedType, onConverted }) {
  const [open, setOpen] = useState(false);
  const [wards, setWards] = useState([]);
  const [ward, setWard] = useState("");
  const [beds, setBeds] = useState([]);
  const [bed, setBed] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchIpdBedTypes()
      .then((list) => setWards(list.filter((w) => w.bedTypeCode !== wardBedType)))
      .catch(() => setWards([]));
  }, [open, wardBedType]);

  useEffect(() => {
    setBed("");
    if (!ward) { setBeds([]); return; }
    fetchIpdBeds(ward).then((list) => setBeds(list.filter((b) => b.isAvailable))).catch(() => setBeds([]));
  }, [ward]);

  const convert = async () => {
    setBusy(true);
    try {
      if (!(await openTransferFor(session.admission_id))) {
        const raised = await raiseTransferRequest({
          admissionId: session.admission_id,
          fromBedCode: session.bed_code,
          toBedCode: bed,
          transferType: "cross_ward",
          initiatedBy: "hospital",
          hospitalTransferReason: "clinical",
          reason: "Day care stay over its shift - converted to an inpatient stay",
        });
        if (raised?.status !== "Success") {
          toast.error(raised?.message || "Could not raise the bed transfer");
          return;
        }
      }
      await changeAdmissionToGeneral(session);
      const res = await markConverted(session.id);
      if (!res.ok) {
        toast.error(res.message || "Could not mark the session converted");
        return;
      }
      toast.success("Converted. Approve and execute the bed transfer on the Bed Transfer screen.");
      setOpen(false);
      onConverted();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Could not convert the stay");
    } finally {
      setBusy(false);
    }
  };

  const { flags = {} } = session;
  return (
    <IpdSectionCard title="Stay longer than planned">
      <Box sx={{ display: "grid", gap: 1.5 }}>
        <Typography sx={{
          fontSize: 13,
          color: flags.must_convert ? IPD_COLORS.error.text : flags.overrun ? IPD_COLORS.warning.text : IPD_COLORS.textSecondary,
        }}
        >
          {flags.must_convert
            ? "Over 24 hours since admission: this is no longer day care and must be converted."
            : flags.overrun
              ? "Past the end of the shift. If the patient must stay, convert to an inpatient stay."
              : "If the patient has to stay, convert the session to an inpatient stay."}
        </Typography>
        <IpdButton size="small" variant={flags.must_convert ? "contained" : "outlined"} tone="amber"
          sx={{ justifySelf: "start" }} onClick={() => setOpen(true)}>
          Convert to inpatient
        </IpdButton>
      </Box>
      {open && (
        <Dialog open onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Convert to an inpatient stay</DialogTitle>
          <DialogContent sx={{ display: "grid", gap: 2 }}>
            <IpdFormField label="Ward" required>
              <IpdTextField select fullWidth value={ward} onChange={(e) => setWard(e.target.value)}>
                {wards.map((w) => <MenuItem key={w.bedTypeCode} value={w.bedTypeCode}>{w.bedTypeName}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Bed" required helperText={ward && !beds.length ? "No free bed in this ward" : ""}>
              <IpdTextField select fullWidth value={bed} disabled={!beds.length} onChange={(e) => setBed(e.target.value)}>
                {beds.map((b) => <MenuItem key={b.bedNo} value={b.bedNo}>{b.bedNo}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <IpdButton variant="outlined" onClick={() => setOpen(false)}>Close</IpdButton>
            <IpdButton tone="amber" disabled={busy || !bed} onClick={convert}>Convert</IpdButton>
          </DialogActions>
        </Dialog>
      )}
    </IpdSectionCard>
  );
}
